import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import api from '../../services/api';

export default function AdminCaseStudies() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCase, setEditingCase] = useState(null);
  const [saveToast, setSaveToast] = useState(false);

  useEffect(() => {
    api.getCmsCaseStudies()
      .then(res => setCases(res.case_studies || []))
      .catch(err => console.error('Error loading case studies:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingCase.isNew) {
        const res = await api.createCmsCaseStudy(editingCase);
        setCases([{ ...editingCase, id: res.id }, ...cases]);
      } else {
        await api.updateCmsCaseStudy(editingCase.id, editingCase);
        setCases(cases.map(c => c.id === editingCase.id ? editingCase : c));
      }
      setEditingCase(null);
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2500);
    } catch (err) {
      alert(err.message || 'Failed to save case study.');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this case study?')) return;
    await api.deleteCmsCaseStudy(id);
    setCases(cases.filter(c => c.id !== id));
  };

  if (loading) {
    return <p className="text-zinc-500 text-sm">Loading case studies...</p>;
  }

  return (
    <div className="space-y-8 font-sans">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-serif font-bold text-white">Case Studies & Client Work</h2>
          <p className="text-xs text-zinc-500 mt-1 font-light">Manage featured case studies, performance metrics, and commercial evidence.</p>
        </div>
        <button
          onClick={() => setEditingCase({ isNew: true, client: '', title: '', category: 'Financial Services', metrics: '', summary: '', services: [], image_url: '', featured: false })}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
        >
          <SafeIcon name="Plus" className="w-4 h-4" />
          Add Case Study
        </button>
      </div>

      {saveToast && (
        <div className="p-4 bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 text-xs rounded-xl flex items-center gap-2">
          <SafeIcon name="CheckCircle" className="w-4 h-4" />
          Case study portfolio updated successfully.
        </div>
      )}

      {/* Case studies grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cases.map(cs => (
          <div key={cs.id} className="bg-zinc-950 border border-zinc-900 rounded-3xl p-6 flex flex-col justify-between shadow-xl space-y-6">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-[9px] uppercase font-bold tracking-widest text-blue-400 bg-blue-900/20 border border-blue-500/20 px-2.5 py-1 rounded">
                  {cs.category}
                </span>
                {cs.featured && (
                  <span className="text-[9px] uppercase font-bold text-zinc-500 bg-zinc-900 px-2 py-0.5 rounded">
                    Featured
                  </span>
                )}
              </div>

              <h3 className="text-base font-bold text-white leading-snug">{cs.title}</h3>
              <p className="text-xs text-zinc-400 font-light leading-relaxed">{cs.summary}</p>

              <div className="p-3 bg-zinc-900/50 rounded-xl border border-zinc-900">
                <p className="text-[9px] uppercase font-bold text-zinc-500">Key Performance Metric</p>
                <p className="text-xs font-bold text-white mt-0.5">{cs.metrics}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-900">
              <span className="text-[10px] text-zinc-500 font-bold">{cs.client}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingCase({ ...cs })}
                  className="p-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded-lg text-xs"
                >
                  <SafeIcon name="Edit" className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(cs.id)}
                  className="p-2 bg-zinc-900 hover:bg-red-950 text-zinc-500 hover:text-red-400 rounded-lg text-xs"
                >
                  <SafeIcon name="Trash2" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {cases.length === 0 && (
          <p className="text-xs text-zinc-600 font-light col-span-full">No case studies yet. Add one to feature it on the agency site.</p>
        )}
      </div>

      {/* Case Study Modal */}
      {editingCase && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-lg w-full p-8 space-y-6 shadow-2xl">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">{editingCase.isNew ? 'New Case Study' : 'Edit Case Study'}</h3>
              <button onClick={() => setEditingCase(null)} className="text-zinc-500 hover:text-white">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Client Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingCase.client}
                  onChange={e => setEditingCase({ ...editingCase, client: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Case Study Headline</label>
                <textarea
                  rows={2}
                  className="input-field resize-none"
                  value={editingCase.title}
                  onChange={e => setEditingCase({ ...editingCase, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Industry Category</label>
                <input
                  type="text"
                  className="input-field"
                  value={editingCase.category}
                  onChange={e => setEditingCase({ ...editingCase, category: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Key Results / Metrics</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. +340% ROAS • ₦120M Revenue"
                  value={editingCase.metrics}
                  onChange={e => setEditingCase({ ...editingCase, metrics: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Strategic Summary</label>
                <textarea
                  rows={3}
                  className="input-field resize-none"
                  value={editingCase.summary}
                  onChange={e => setEditingCase({ ...editingCase, summary: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Services (comma-separated)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. Brand Strategy, Visual Identity, Digital Experience"
                  value={(editingCase.services || []).join(', ')}
                  onChange={e => setEditingCase({ ...editingCase, services: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Cover Image URL</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="https://... (leave blank to show a placeholder)"
                  value={editingCase.image_url || ''}
                  onChange={e => setEditingCase({ ...editingCase, image_url: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="feat-check"
                  checked={editingCase.featured || false}
                  onChange={e => setEditingCase({ ...editingCase, featured: e.target.checked })}
                  className="rounded bg-zinc-900 border-zinc-800 text-blue-600"
                />
                <label htmlFor="feat-check" className="text-xs text-zinc-300">Feature on agency homepage</label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button
                  type="button"
                  onClick={() => setEditingCase(null)}
                  className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20"
                >
                  Save Case Study
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
